/* AppraisalComponents/hooks/useTileRescrewRowCascade.js
 * Phase C · Row-scoped Tile Rescrew wire-in (2026-02c).
 *
 * Consumer of the 3 `FST-Rescrew-Tile-*` engine rules. When the current
 * appraisal is a tile-rescrew quote AND a Fastening line has a
 * `row_position` set (Bottom / Ridge / Barge), fire a row-scoped
 * `evaluateCascade` with `{quote_type, row_position}` anchors, then
 * back-fill the winning rate row onto the line.
 *
 * Mirrors the row-scoped pattern proven in the Flashing cascade
 * (AppraisalComponentsTab.js:2030-2110) · module-level cache in
 * useCascadeEngine dedupes per (quote_type, row_position) tuple across
 * renders so N rows with the same position issue at most 1 backend call.
 *
 * Contract:
 *   Reads:  loading, quoteType, rates.Fastening, groups.Fastening
 *   Writes: setGroups(prev => ...) — mutates only prev.Fastening
 *
 * Gate: only fires when `quoteType` matches `rescrew.*tile` /
 * `tile.*rescrew` (the same regex the backend rules use). No-op
 * otherwise · sibling hook `useFasteningCascade` owns the LongRun-Iron
 * / Iron-Rescrew paths.
 */
import { useEffect } from 'react';
import { evaluateCascade } from '@/hooks/useCascadeEngine';

const TILE_RESCREW_RE = /rescrew.*(colortile|colourtile|decramastic|tile)|(colortile|colourtile|decramastic|tile).*rescrew/i;

export function useTileRescrewRowCascade({
  loading,
  quoteType,
  rates,
  groupsFasteningLength,   // groups.Fastening?.length — cheap dep
  groupsFastening,         // groups.Fastening — reference-identity dep
  setGroups,
}) {
  useEffect(() => {
    if (loading) return;
    if (!quoteType || !TILE_RESCREW_RE.test(quoteType)) return;
    const fasteningRates = rates.Fastening || [];
    if (!fasteningRates.length) return;
    const lines = groupsFastening || [];
    if (!lines.length) return;

    // Gather distinct row_positions so we ask the engine ONCE per
    // unique tuple. Module-level cache in useCascadeEngine dedupes
    // repeat calls across renders (same pattern as Flashing).
    const positions = new Set();
    lines.forEach(l => {
      if (l.override) return;
      const pos = String(l.row_position || '').trim().toLowerCase();
      if (pos && ['bottom', 'ridge', 'barge'].includes(pos)) positions.add(pos);
    });
    if (positions.size === 0) return;

    let cancelled = false;
    (async () => {
      // Fetch engine tokens per position (cached · deduped by module).
      const tokensByPos = {};
      for (const pos of positions) {
        const r = await evaluateCascade({ quote_type: quoteType, row_position: pos });
        const t = r?.cascade?.rate_picks?.Fastening?.tokens;
        if (Array.isArray(t) && t.length) tokensByPos[pos] = t;
      }
      if (cancelled) return;
      if (!Object.keys(tokensByPos).length) return;

      const findByTokens = (tokens) => fasteningRates.find(r => {
        const tn = String(r.type_name || '').toLowerCase();
        return tokens.every(t => tn.includes(String(t).toLowerCase()));
      });

      setGroups(prev => {
        const rows = prev.Fastening || [];
        if (!rows.length) return prev;
        let touched = false;
        const next = rows.map(l => {
          if (l.override) return l;
          const pos = String(l.row_position || '').trim().toLowerCase();
          if (!pos) return l;
          const tokens = tokensByPos[pos];
          if (!tokens) return l;
          const card = findByTokens(tokens);
          if (!card) return l;

          const typeMatchesRateRow = !!l.type_name
            && fasteningRates.some(r => r.type_name === l.type_name);
          const cascadable = !l.type_name || !typeMatchesRateRow;
          if (!cascadable) return l;

          const patch = { type_name: card.type_name };
          if (!l.buy_cost && card.buy_cost)               patch.buy_cost        = card.buy_cost;
          if (!l.materials_rate && card.materials_rate)   patch.materials_rate  = card.materials_rate;
          if (!l.labour_rate && card.labour_rate)         patch.labour_rate     = card.labour_rate;
          if (!l.contractor_rate && card.contractor_rate) patch.contractor_rate = card.contractor_rate;
          if (!l.supplier && card.supplier)               patch.supplier        = card.supplier;
          if (!l.supplier_code && card.supplier_code)     patch.supplier_code   = card.supplier_code;
          if (!l.materials_desc && card.materials_desc)   patch.materials_desc  = card.materials_desc;
          if (!l.labour_details && card.labour_details)   patch.labour_details  = card.labour_details;
          if (Object.keys(patch).length === 0) return l;
          touched = true;
          return { ...l, ...patch };
        });
        return touched ? { ...prev, Fastening: next } : prev;
      });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, quoteType, groupsFasteningLength, groupsFastening]);
}
